/* import __COLOCATED_TEMPLATE__ from './personal-info.hbs'; */
import Component from '@glimmer/component';
import { action, getProperties } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { task, dropTask } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { handleErrorResponse } from 'sh-www/utils/handles-errors';
import { US_STATES } from 'sh-www/constants/us-states';
import { HYPERWALLET_USER } from 'sh-www/models/hyperwallet-user';
import { validateSchema } from 'sh-www/utils/validators';
import { object, string } from 'yup';

const CLASS_NAME = 'SH-c-payout-add-method-personal-info';

const SCHEMA = object({
  firstName: string()
    .required('Please enter your first name'),

  lastName: string()
    .required('Please enter your last name'),

  profileType: string()
    .required('Please select a profile type'),

  businessName: string().when(['profileType'], {
    is: (profileType) => profileType === HYPERWALLET_USER.PROFILE_TYPES.BUSINESS,
    then: (schema) =>
      schema.required('Please enter a business name'),
  }),

  businessType: string().when(['profileType'], {
    is: (profileType) => profileType === HYPERWALLET_USER.PROFILE_TYPES.BUSINESS,
    then: (schema) =>
      schema.required('Please enter a business type'),
  }),

  dateOfBirth: string()
    .matches(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/ , 'Please enter a date of birth with the format YYYY-MM-DD')
    .trim('Please do not include leading or trailing spaces')
    .strict()
    .required('Please enter a date of birth'),
  
  country: string()
    .required('Please select a country'),
  
  stateProvince: string()
    .required('Please select a state/province'),

  addressLine1: string()
    .required('Please enter an address'),
  
  addressLine2: string()
    .optional(),

  city: string()
    .required('Please enter a city'),

  postalCode: string()
    .required('Please enter a postal code'),
});

const PROFILE_TYPE_OPTIONS = Object.keys(HYPERWALLET_USER.PROFILE_TYPES).map((key) => ({
  key: HYPERWALLET_USER.PROFILE_TYPE_NAMES[key],
  model: { id: key, label: HYPERWALLET_USER.PROFILE_TYPE_NAMES[key] },
}));

const BUSINESS_TYPE_OPTIONS = Object.keys(HYPERWALLET_USER.BUSINESS_TYPES).map((key) => ({
  key: HYPERWALLET_USER.BUSINESS_TYPE_NAMES[key],
  model: { id: key, label: HYPERWALLET_USER.BUSINESS_TYPE_NAMES[key] },
}));

class PayoutPersonalInfo extends Component {
  @service store;
  @service session;
  @service shData;
  @service('commands/hyperwallet-user-commands') hyperwalletUserCommands;

  @tracked firstName = '';
  @tracked lastName = '';
  @tracked profileType = '';
  @tracked businessName = '';
  @tracked businessType = '';
  @tracked dateOfBirth = '';
  @tracked country = '';
  @tracked stateProvince = '';
  @tracked addressLine1 = '';
  @tracked addressLine2 = '';
  @tracked city = '';
  @tracked postalCode = '';
  @tracked countries = this.args.countries ?? [];
  @tracked errors = {};

  get className() {
    return CLASS_NAME;
  }

  /** @type {Boolean} Whether to lock fields related to profile type (HW forbids changing these). */
  get lockProfileType() {
    return !isEmpty(this.args.hyperwalletUser);
  }

  get profileTypeOptions() {
    return PROFILE_TYPE_OPTIONS;
  }

  get businessTypeOptions() {
    return BUSINESS_TYPE_OPTIONS;
  }

  get countryOptions() {
    return this.args.countries.map((country) => ({
      key: country.name,
      model: country,
    }));
  }

  get countrySelectedOption() {
    const selectedCountry = this.args.countries
      .find((country) => country.code === this.country);

    return selectedCountry ? selectedCountry.name : '';
  }

  get isCountryUS() {
    return this.country === 'US';
  }

  get stateProvinceSelectedOption() {
    const stateProvince = this.stateProvince;

    if (this.isCountryUS) {
      return stateProvince ? US_STATES[stateProvince] : '';
    }

    return stateProvince;
  }

  get profileTypeSelectedOption() {
    const id = this.profileType;
    return id ? HYPERWALLET_USER.PROFILE_TYPE_NAMES[id] : '';
  }

  get businessTypeSelectedOption() {
    const id = this.businessType;
    return id ? HYPERWALLET_USER.BUSINESS_TYPE_NAMES[id] : '';
  }

  get isBusinessProfileType() {
    return this.profileType === HYPERWALLET_USER.PROFILE_TYPES.BUSINESS;
  }

  dataHasChanged(data){
    const hyperwalletUser = this.args.hyperwalletUser;
    if (!hyperwalletUser) return true;

    for (const fieldName of Object.keys(data)) {
      if (data[fieldName] !== hyperwalletUser[fieldName]) {
        return true;
      }
    }

    return false;
  }

  @dropTask
  *submit(values) {
    const userId = this.session.currentUser.id;
    const submitCommand = this.args.hyperwalletUser
      ? 'updateHyperwalletUser'
      : 'createHyperwalletUser';

    try {
      const response = yield this.hyperwalletUserCommands[submitCommand](userId, values);
      this.shData.pushResponseToStore(response);
      this.args.onSubmitSuccess();
    } catch (error) {
      handleErrorResponse(this, error, Object.keys(values));
    }
  }

  @task
  *loadCountries() {
    this.countries = yield this.store.findAll('country');
  }

  @action
  initializeData() {
    const { hyperwalletUser, countries } = this.args;

    if (!countries.length) {
      this.loadCountries.perform();
    }

    if (!hyperwalletUser) {
      const { currentUser } = this.session;

      this.firstName = currentUser.firstName;
      this.lastName = currentUser.lastName;
      this.country = this.args.countryCode;
      return;
    }

    this.firstName = hyperwalletUser.firstName;
    this.lastName = hyperwalletUser.lastName;
    this.profileType = hyperwalletUser.profileType;
    this.dateOfBirth = hyperwalletUser.dateOfBirth;
    this.country = hyperwalletUser.country;
    this.stateProvince = hyperwalletUser.stateProvince;
    this.addressLine1 = hyperwalletUser.addressLine1;
    this.addressLine2 = hyperwalletUser.addressLine2;
    this.city = hyperwalletUser.city;
    this.postalCode = hyperwalletUser.postalCode;

    if (this.isBusinessProfileType) {
      this.businessName = hyperwalletUser.businessName;
      this.businessType = hyperwalletUser.businessType;
    }
  }

  @action
  onInput(field, value) {
    this[field] = value;

    if (this.errors?.[field]) {
      this.errors = { ...this.errors, [field]: undefined };
    }
  }

  @action
  onProfileTypeSelect(profileType) {
    this.profileType = profileType.id;

    if (profileType.id === HYPERWALLET_USER.PROFILE_TYPES.INDIVIDUAL) {
      this.businessName = '';
      this.businessType = '';
      this.errors.businessName = undefined;
      this.errors.businessType = undefined;
    }

    this.errors.profileType = undefined;
  }

  @action
  onBusinessTypeSelect(businessType) {
    this.businessType = businessType.id;
    this.errors.businessType = undefined;
  }

  @action
  onCountrySelect(country) {
    this.country = country.code;
    this.stateProvince = '';
    this.errors.country = undefined;
    this.errors.stateProvince = undefined;
  }

  @action
  onUsStateSelect(state) {
    this.stateProvince = state.code;
    this.errors.stateProvince = undefined;
  }

  @action
  onConfirm() {
    const [values, validationErrors = null] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);

    this.errors = validationErrors;
    if (this.errors) {
      return;
    }

    if (!this.isBusinessProfileType) {
      delete values.businessName;
      delete values.businessType;
    }

    if (!this.dataHasChanged(values)) {
      this.args.onSubmitSuccess();
      return;
    }

    this.submit.perform(values);
  }
}

export default PayoutPersonalInfo;
